import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { Save } from '@factorialco/f0-react/icons/app'
import { PoTemplateDetailDocument, SavePoTemplateDocument } from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import FieldEditor from 'modules/procurement/components/poTemplates/FieldEditor'
import type { NumerationState } from 'modules/procurement/components/poTemplates/FieldEditor'
import { mergeFields } from 'modules/procurement/components/poTemplates/FieldEditor/mergeFields'
import {
  toCustomFieldType,
  type MergedField,
  type SectionType,
} from 'modules/procurement/components/poTemplates/FieldEditor/types'

function PoTemplateDetailPageContent() {
  const { id } = useParams<{ id: string }>()
  const [localVisibility, setLocalVisibility] = useState<Map<string, boolean> | null>(null)
  const [localCustomFields, setLocalCustomFields] = useState<MergedField[]>([])
  const [localEdits, setLocalEdits] = useState<Map<string, { label: string; options: string[] }>>(
    new Map()
  )
  const [deletedFieldKeys, setDeletedFieldKeys] = useState<Set<string>>(new Set())
  const [localPositions, setLocalPositions] = useState<Map<string, number> | null>(null)
  const [numeration, setNumeration] = useState<NumerationState>({ prefix: '', nextNumber: null })
  const [numerationErrors, setNumerationErrors] = useState<{
    prefix?: string
    nextNumber?: string
  }>({})
  const [hasLocalChanges, setHasLocalChanges] = useState(false)

  // Fetch template detail + predefined fields catalog in a single query
  const {
    data: detailData,
    loading,
    error: detailError,
  } = useQuery(PoTemplateDetailDocument, {
    variables: { poTemplateId: id ?? '' },
    skip: !id,
  })

  const [savePoTemplate, { loading: isSaving }] = useMutation(SavePoTemplateDocument, {
    extractorFn: (data) => data.procurementMutations.saveProcurementPoTemplate,
    refetchQueries: [PoTemplateDetailDocument],
  })

  const poTemplate = detailData?.procurement?.poTemplatesConnection?.edges?.[0]?.node
  const catalogFields = useMemo(
    () =>
      detailData?.procurement?.poTemplatePredefinedFieldsConnection?.edges?.map((e) => e.node) ??
      [],
    [detailData]
  )

  // Sync numeration from server data when it loads
  useEffect(() => {
    if (poTemplate) {
      setNumeration({
        prefix: poTemplate.poNumberPrefix ?? '',
        nextNumber: poTemplate.nextNumber,
      })
    }
  }, [poTemplate])

  // Reset all local change state when navigating between templates so pending
  // edits/deletes/custom fields from one template do not leak into another.
  useEffect(() => {
    setLocalVisibility(null)
    setLocalCustomFields([])
    setLocalEdits(new Map())
    setDeletedFieldKeys(new Set())
    setLocalPositions(null)
    setNumerationErrors({})
    setHasLocalChanges(false)
  }, [id])

  const currentVersion = useMemo(() => {
    if (!poTemplate) return undefined
    const versions = poTemplate.poTemplateVersionsConnection.edges.map((e) => e.node)
    return versions.find((v) => v.status === 'draft') ?? versions.find((v) => v.status === 'active')
  }, [poTemplate])

  const serverFieldDefinitions = useMemo(
    () => currentVersion?.poTemplateFieldDefinitionsConnection.edges.map((e) => e.node) ?? [],
    [currentVersion]
  )

  // Merge predefined catalog with stored definitions
  const mergedFields = useMemo(
    () => mergeFields(catalogFields, serverFieldDefinitions),
    [catalogFields, serverFieldDefinitions]
  )

  // Combine server-merged fields with locally-added custom fields, then apply visibility/edit overrides
  const fieldsWithLocalState = useMemo(() => {
    const all = [...mergedFields, ...localCustomFields]
    return all
      .filter((f) => !deletedFieldKeys.has(f.key))
      .map((f) => {
        const edit = localEdits.get(f.key)
        const visibilityOverride = localVisibility?.get(f.key)
        const positionOverride = localPositions?.get(f.key)
        let next = f
        if (edit) {
          next = { ...next, displayLabel: edit.label, options: edit.options }
        }
        if (visibilityOverride !== undefined) {
          next = { ...next, visible: visibilityOverride }
        }
        if (positionOverride !== undefined) {
          next = { ...next, position: positionOverride }
        }
        return next
      })
  }, [
    mergedFields,
    localCustomFields,
    localVisibility,
    localEdits,
    deletedFieldKeys,
    localPositions,
  ])

  const handleToggleVisibility = useCallback((fieldKey: string, newVisible: boolean) => {
    setLocalVisibility((prev) => {
      const next = new Map(prev ?? [])
      next.set(fieldKey, newVisible)
      return next
    })
    setHasLocalChanges(true)
  }, [])

  const handleAddCustomField = useCallback(
    (section: SectionType, label: string, fieldType: 'text' | 'select', options: string[]) => {
      const fieldKey = crypto.randomUUID()
      const sectionFields = fieldsWithLocalState.filter((f) => f.sectionType === section)
      const maxPosition = sectionFields.reduce((max, f) => Math.max(max, f.position), -1)

      const newField: MergedField = {
        key: `custom:${fieldKey}`,
        sectionType: section,
        fieldKey,
        displayLabel: label,
        fieldType,
        predefined: false,
        visible: true,
        required: false,
        computed: false,
        position: maxPosition + 1,
        storedId: null,
        defaultValue: null,
        options,
      }

      setLocalCustomFields((prev) => [...prev, newField])
      setHasLocalChanges(true)
    },
    [fieldsWithLocalState]
  )

  const handleEditCustomField = useCallback((key: string, label: string, options: string[]) => {
    setLocalCustomFields((prev) => {
      // If the key targets an unsaved local custom field, apply the change inline
      // and leave `localEdits` alone.
      if (prev.some((f) => f.key === key)) {
        return prev.map((f) => (f.key === key ? { ...f, displayLabel: label, options } : f))
      }
      // Otherwise it targets a server-stored field: track the edit separately so we
      // don't trigger an unnecessary `localCustomFields` state update / re-render.
      setLocalEdits((edits) => {
        const updated = new Map(edits)
        updated.set(key, { label, options })
        return updated
      })
      return prev
    })
    setHasLocalChanges(true)
  }, [])

  const handleDeleteCustomField = useCallback((key: string) => {
    setLocalCustomFields((prev) => {
      const next = prev.filter((f) => f.key !== key)
      // If the field was an unsaved local custom field, dropping it from the
      // local list is enough — no server-side delete needed.
      if (next.length !== prev.length) return next
      // Otherwise it targets a server-stored field: mark it for deletion so
      // it gets filtered out of the save payload.
      setDeletedFieldKeys((deleted) => {
        const updated = new Set(deleted)
        updated.add(key)
        return updated
      })
      // Drop any pending local edit for the same field.
      setLocalEdits((edits) => {
        if (!edits.has(key)) return edits
        const updated = new Map(edits)
        updated.delete(key)
        return updated
      })
      return prev
    })
    setHasLocalChanges(true)
  }, [])

  const handleReorderFields = useCallback(
    (section: SectionType, sourceIndex: number, destinationIndex: number) => {
      // Read the section's current displayed order from `fieldsWithLocalState`
      // (visible and hidden fields are both draggable in the editor) so any
      // in-flight position overrides are preserved across consecutive moves.
      const sectionFields = fieldsWithLocalState
        .filter((f) => f.sectionType === section)
        .sort((a, b) => a.position - b.position)

      if (sourceIndex === destinationIndex) return
      if (sourceIndex < 0 || sourceIndex >= sectionFields.length) return
      if (destinationIndex < 0 || destinationIndex >= sectionFields.length) return

      const reordered = [...sectionFields]
      const [moved] = reordered.splice(sourceIndex, 1)
      if (!moved) return
      reordered.splice(destinationIndex, 0, moved)

      setLocalPositions((prev) => {
        const next = new Map(prev ?? [])
        reordered.forEach((field, index) => {
          next.set(field.key, index)
        })
        return next
      })
      setHasLocalChanges(true)
    },
    [fieldsWithLocalState]
  )

  const handleNumerationChange = useCallback((next: NumerationState) => {
    setNumeration((prev) => {
      setNumerationErrors((errs) => {
        const updated = { ...errs }
        if (prev.prefix !== next.prefix) delete updated.prefix
        if (prev.nextNumber !== next.nextNumber) delete updated.nextNumber
        return updated
      })
      return next
    })
    setHasLocalChanges(true)
  }, [])

  const handleSave = useCallback(async () => {
    if (!poTemplate || !id) return

    // Client-side validation: block save and surface inline errors when required
    // numeration fields are empty. Without this, `|| undefined` / `?? undefined` would
    // silently omit them from the mutation input and the server would skip updating them,
    // leading to a misleading "saved" experience for the user.
    const clientErrors: { prefix?: string; nextNumber?: string } = {}
    const trimmedPrefix = numeration.prefix.trim()
    if (!trimmedPrefix) {
      clientErrors.prefix = i18n.t(
        'procurement.purchase_requests.settings.po_templates.detail.numeration_prefix_required'
      )
    }
    if (numeration.nextNumber == null) {
      clientErrors.nextNumber = i18n.t(
        'procurement.purchase_requests.settings.po_templates.detail.numeration_next_number_required'
      )
    }
    if (clientErrors.prefix || clientErrors.nextNumber) {
      setNumerationErrors(clientErrors)
      return
    }

    const action = await savePoTemplate({
      variables: {
        id,
        fieldDefinitions: fieldsWithLocalState.map((f) => ({
          sectionType: f.sectionType,
          fieldKey: f.fieldKey,
          visible: f.visible,
          position: f.position,
          label: f.predefined ? undefined : f.displayLabel,
          fieldType: f.predefined ? undefined : toCustomFieldType(f.fieldType),
          defaultValue: f.defaultValue ?? undefined,
          options: f.options.length > 0 ? f.options : undefined,
        })),
        poNumberPrefix: trimmedPrefix,
        nextNumber: numeration.nextNumber,
        poNumberResetYearly: poTemplate.poNumberResetYearly,
      },
    })

    const result = handleResult(action, (errors) => {
      const inline: { prefix?: string; nextNumber?: string } = {}
      const otherMessages: string[] = []
      for (const [field, message] of Object.entries(errors)) {
        if (field === 'nextNumber') {
          inline.nextNumber = message
        } else if (field === 'poNumberPrefix') {
          inline.prefix = message
        } else {
          otherMessages.push(message)
        }
      }
      setNumerationErrors(inline)
      if (otherMessages.length) {
        app.setFlash({ type: 'wrong', message: otherMessages.join(', ') })
      }
    })

    if (result.success) {
      setLocalVisibility(null)
      setLocalCustomFields([])
      setLocalEdits(new Map())
      setDeletedFieldKeys(new Set())
      setLocalPositions(null)
      setHasLocalChanges(false)
      setNumerationErrors({})
      app.setFlash({
        type: 'correct',
        message: i18n.t('procurement.purchase_requests.settings.po_templates.detail.save_success'),
      })
    }
  }, [poTemplate, id, fieldsWithLocalState, numeration, savePoTemplate])

  return (
    <ResponsivePage.Body
      authorized
      error={detailError}
      loading={loading}
      showHeader={false}
      variant="fullscreen"
    >
      <ResourceHeader
        title={poTemplate?.name ?? ''}
        secondaryActions={[
          {
            label: i18n.t('common.save'),
            onClick: handleSave,
            disabled: !hasLocalChanges || isSaving,
            icon: Save,
          },
        ]}
      />

      <FieldEditor
        fields={fieldsWithLocalState}
        numeration={numeration}
        numerationErrors={numerationErrors}
        onToggleVisibility={handleToggleVisibility}
        onAddCustomField={handleAddCustomField}
        onEditCustomField={handleEditCustomField}
        onDeleteCustomField={handleDeleteCustomField}
        onReorderFields={handleReorderFields}
        onNumerationChange={handleNumerationChange}
      />
    </ResponsivePage.Body>
  )
}

export default PoTemplateDetailPageContent
